import { useEffect, useRef, useState } from 'react';
import { altaAbiertaDelConflicto, type Alta, type Canal, type DatosAlta, type TipoCanal } from '../../api/ops/canales';
import { ErrorApi } from '../../api/problema';
import { useAvisoBreve } from '../../componentes/avisoBreve';
import { Boton } from '../../componentes/Boton';
import { Campo } from '../../componentes/Campo';
import { Chip, type EstadoChip } from '../../componentes/Chip';
import { Desplegable } from '../../componentes/Desplegable';
import { Icono } from '../../componentes/Icono';
import type { NombreIcono } from '../../componentes/iconos';
import { OpcionRadio } from '../../componentes/OpcionRadio';
import { PanelLateral } from '../../componentes/PanelLateral';
import { altaMeta } from '../../logica/altaMeta';
import { altaYCloud, type SucesoYCloud } from '../../logica/altaYCloud';
import { avisoBreveDeError } from '../../logica/estados';
import { usePermisos } from '../../permisos/permisos';
import type { PropsApartado } from './Canales';
import { RUTA_CANALES_DE_ENVIO } from './ListaCanales';
import { errorToken, lineaElegir, MOTIVOS, motivoDeFallo, NOTAS_ALTA, PASOS, permitido, PROVEEDOR, TIPO, TIPOS } from './reglas';

/**
 * 11.2 Conectar un canal (RV Panel). Elegir el tipo con el bloque que necesita (`listEntitlements` y la tabla de
 * `startChannelConnection`). **WhatsApp con el enlace de alta de YCloud** (G694, G695): «Entrar con Meta» →
 * `startChannelConnection` → ventana del SDK de YCloud con `ycloud_onboarding` (`logica/altaYCloud.ts`; si el navegador la
 * bloquea, «Abrir en otra pestaña») → el panel consulta `getChannelConnection` cada pocos segundos hasta que el aviso de
 * YCloud cierra el alta («Conectado; confirmando…» en cuanto la ventana dice `CONNECTED`). Cerrar la ventana no cancela el
 * alta: se puede volver a abrir hasta que caduca. **Instagram con la ventana de Meta** (`logica/altaMeta.ts`) →
 * `completeChannelConnection` con el código y el `state`; si la persona la cierra sin terminar, el panel cancela el alta y
 * dice «Has cerrado la ventana de Meta». Telegram con el token del bot; email solo con el nombre del remitente:
 * sale siempre desde `avisos@reservaria.app`, sin dominio propio ni registros DNS (G747). Voz: próximamente (no se conecta). Reconectar (`replaces_channel_id`) desde la
 * ficha de un canal con una incidencia que lo pide.
 *
 * Un alta de WhatsApp o Instagram que se deja a medias al cerrar el panel se recuerda en memoria (`altaPrevia`; nunca en el
 * navegador: el enlace de YCloud es una credencial temporal) y al volver a ese tipo se sigue con ella («Abrir otra vez» o
 * «Cancelar el alta»). Si la API dice que ya hay un alta abierta de ese tipo (`409 conflict`
 * con su `connection_id`, G687), «Seguir con la que empecé» la retoma (`getChannelConnection`) y «Cancelarla» la cancela.
 * El chat de la web no se conecta aquí (G685).
 */
type Paso = 'elegir' | 'form' | 'meta' | 'res';
interface Vuelta {
    clase: 'correcta' | 'pendiente' | 'cancelada' | 'caducada' | 'error';
    motivo?: string;
    /** El alta abierta del mismo tipo (`409 conflict`). */
    abiertaId?: string;
}

type Pulsable = 'mensajes' | 'plantillas' | 'seguir' | 'cancelarla' | 'plan';
/** Lo que la ventana de YCloud ha dicho (solo cambia los textos: el resultado lo da `getChannelConnection`). */
type EstadoYCloud = 'abierta' | 'confirmando' | 'bloqueada' | 'cerrada' | 'incompleta';

/** Cada cuánto se consulta el alta de WhatsApp mientras se espera el aviso de YCloud. */
export const CONSULTA_ALTA_MS = 3000;

const HORA = new Intl.DateTimeFormat('es-ES', { hour: '2-digit', minute: '2-digit' });

export function Conectar(
    props: PropsApartado & {
        tipoInicial: TipoCanal | null;
        reemplaza: Canal | null;
        altaPrevia: Alta | null;
        onAltaAbierta: (a: Alta | null) => void;
        /** Cierra el panel; con texto, el aviso «hecho» de la lista. */
        onCerrar: (texto?: string) => void;
        onVerCanal: (id: string) => void;
    },
) {
    const { ops, negocio, movil, soloLectura, navegar, datos, recargarDatos, membresia, nombreNegocio, irA, tipoInicial, reemplaza, altaPrevia, onAltaAbierta, onCerrar, onVerCanal } = props;
    const { puede } = usePermisos();
    const avisar = useAvisoBreve();
    const conecta = puede('canales.canales.gestionar') && !soloLectura;
    const propietario = membresia?.is_owner === true;
    const [ahora] = useState(() => Date.now());
    const sigueAbierta = (t: TipoCanal | null) => altaPrevia !== null && altaPrevia.channel_type === t && reemplaza === null && new Date(altaPrevia.expires_at).getTime() > ahora;
    const inicialPermitido = tipoInicial !== null && permitido(tipoInicial, datos.bloques).ok;
    const [tipo, setTipo] = useState<TipoCanal | null>(inicialPermitido ? tipoInicial : null);
    const [paso, setPaso] = useState<Paso>(inicialPermitido ? (sigueAbierta(tipoInicial) ? 'meta' : 'form') : 'elegir');
    const [bloqueado, setBloqueado] = useState<TipoCanal | null>(tipoInicial !== null && !inicialPermitido ? tipoInicial : null);
    const [sede, setSede] = useState('');
    const [token, setToken] = useState('');
    const [errToken, setErrToken] = useState<string | null>(null);
    const [remitente, setRemitente] = useState(nombreNegocio);
    const [alta, setAlta] = useState<Alta | null>(inicialPermitido && sigueAbierta(tipoInicial) ? altaPrevia : null);
    const [vuelta, setVuelta] = useState<Vuelta | null>(null);
    const [ocupado, setOcupado] = useState(false);
    const [ycloud, setYcloud] = useState<EstadoYCloud | null>(null);
    // G702: «¿Ya usas este número en la app WhatsApp Business?» (sin respuesta no se sigue).
    const [modo, setModo] = useState<'business_app' | 'api_only' | null>(reemplaza?.whatsapp_mode ?? null);
    const [errModo, setErrModo] = useState(false);
    const [queCambia, setQueCambia] = useState(false);
    // Cada ventana abierta tiene su número: si la persona cancela o sale antes de que vuelva, se ignora.
    const ventana = useRef(0);

    useEffect(() => () => { ventana.current += 1; }, []);

    const nombreTipo = tipo === null ? '' : (TIPO[tipo]?.nombre ?? tipo);
    const canalDe = (id: string | null | undefined) => (id == null ? null : (datos.canales.find((c) => c.id === id) ?? null));

    const terminar = (a: Alta) => {
        setAlta(a);
        if (a.status === 'started' || a.status === 'awaiting_provider') {
            return;
        }
        onAltaAbierta(null);
        if (a.status === 'completed' || a.status === 'verifying') {
            recargarDatos();
        }
        setVuelta(a.status === 'completed' ? { clase: 'correcta' } : a.status === 'verifying' ? { clase: 'pendiente' } : a.status === 'cancelled' ? { clase: 'cancelada' } : a.status === 'expired' ? { clase: 'caducada' } : { clase: 'error', motivo: motivoDeFallo(a) });
        setPaso('res');
    };

    /** Errores de la API al empezar o completar que tienen su vuelta dibujada; el resto, aviso breve con el texto de la API. */
    const fallo = (e: unknown, alCompletar: boolean) => {
        if (e instanceof ErrorApi) {
            const motivo =
                e.codigo === 'whatsapp_account_limit_reached'
                    ? 'limite'
                    : e.codigo === 'provider_error'
                      ? 'proveedor'
                      : e.codigo === 'entitlement_required' && tipo === 'whatsapp'
                        ? 'bloque'
                        : e.codigo === 'conflict' && alCompletar
                          ? tipo === 'telegram'
                              ? 'bot-usado'
                              : 'numero-usado'
                          : e.codigo === 'conflict' && !alCompletar
                            ? 'alta-abierta'
                            : null;
            if (motivo !== null) {
                if (motivo !== 'alta-abierta') {
                    onAltaAbierta(null);
                }
                // La abierta: la que dice el 409 (G687) o, si no la trae, la que el panel recuerda de ese tipo.
                const abiertaId = motivo === 'alta-abierta' ? (altaAbiertaDelConflicto(e) ?? (altaPrevia !== null && altaPrevia.channel_type === tipo ? altaPrevia.id : null)) : null;
                setVuelta(abiertaId === null ? { clase: 'error', motivo } : { clase: 'error', motivo, abiertaId });
                setPaso('res');
                return;
            }
        }
        avisar({ tipo: 'error', texto: avisoBreveDeError(e) ?? (e instanceof ErrorApi ? (e.problema.detail ?? e.problema.title) : 'No se ha podido conectar.') });
    };

    const empezar = async (): Promise<Alta | null> => {
        try {
            return await ops.startChannelConnection(negocio, {
                channel_type: tipo ?? 'email',
                ...(tipo === 'whatsapp' ? { whatsapp_mode: modo ?? 'api_only' } : {}),
                ...(sede === '' || reemplaza !== null ? {} : { location_id: sede }),
                ...(reemplaza === null ? {} : { replaces_channel_id: reemplaza.id }),
            });
        } catch (e) {
            fallo(e, false);
            return null;
        }
    };

    const completar = async (a: Alta, datosAlta: DatosAlta) => {
        try {
            terminar(await ops.completeChannelConnection(negocio, a.id, datosAlta));
        } catch (e) {
            fallo(e, true);
        }
    };

    /** Instagram: la ventana del alta integrada de Meta y `completeChannelConnection` con el código y el `state`. */
    const abrirMeta = async (a: Alta) => {
        const firma = a.meta_signup;
        const numero = ++ventana.current;
        if (firma == null) {
            await ops.cancelChannelConnection(negocio, a.id).catch(() => null);
            onAltaAbierta(null);
            setVuelta({ clase: 'error', motivo: 'proveedor' });
            setPaso('res');
            return;
        }
        const v = await altaMeta.abrir({ app_id: firma.app_id ?? '', config_id: firma.config_id ?? '' });
        if (numero !== ventana.current) {
            return;
        }
        if (v.tipo === 'hecha') {
            setOcupado(true);
            await completar(a, { meta_code: v.codigo, state: firma.state ?? '' });
            setOcupado(false);
            return;
        }
        // Cerrada sin terminar (o sin poder abrirla): el alta se cancela; así no bloquea la siguiente.
        const cancelada = await ops.cancelChannelConnection(negocio, a.id).catch(() => null);
        onAltaAbierta(null);
        if (v.tipo === 'cerrada') {
            if (cancelada === null) {
                setVuelta({ clase: 'cancelada' });
                setPaso('res');
            } else {
                terminar(cancelada);
            }
        } else {
            setVuelta({ clase: 'error', motivo: 'proveedor' });
            setPaso('res');
        }
    };

    /**
     * WhatsApp (G695): mira cómo va el alta. Mientras YCloud no avisa sigue en `awaiting_provider`; en cuanto la cierra
     * (completada, en verificación, fallida o caducada), su vuelta.
     */
    const consultar = async (id: string) => {
        const numero = ventana.current;
        const a = await ops.getChannelConnection(negocio, id).catch(() => null);
        if (a === null || numero !== ventana.current || a.status === 'awaiting_provider' || a.status === 'started') {
            return;
        }
        ventana.current += 1;
        setYcloud(null);
        terminar(a);
    };
    const consultarRef = useRef(consultar);
    useEffect(() => {
        consultarRef.current = consultar;
    });

    // Mientras se espera el aviso de YCloud, se consulta el alta cada pocos segundos (también con la ventana cerrada).
    const esperando = paso === 'meta' && tipo === 'whatsapp' && alta !== null && alta.status === 'awaiting_provider' ? alta.id : null;
    useEffect(() => {
        if (esperando === null) {
            return undefined;
        }
        const reloj = window.setInterval(() => {
            void consultarRef.current(esperando);
        }, CONSULTA_ALTA_MS);
        return () => {
            window.clearInterval(reloj);
        };
    }, [esperando]);

    /** WhatsApp: el enlace de YCloud ya no vale (caducado o usado). Se cancela el alta y se empieza otra. */
    const enlaceNoValido = async (a: Alta) => {
        ventana.current += 1;
        setYcloud(null);
        await ops.cancelChannelConnection(negocio, a.id).catch(() => null);
        onAltaAbierta(null);
        setVuelta({ clase: 'error', motivo: 'enlace' });
        setPaso('res');
    };

    /** WhatsApp (G694, G695): la ventana del SDK de YCloud con el enlace de alta. Cerrarla no cancela el alta. */
    const abrirYCloud = async (a: Alta) => {
        const enlace = a.ycloud_onboarding?.onboarding_url;
        const numero = ++ventana.current;
        if (enlace == null) {
            await ops.cancelChannelConnection(negocio, a.id).catch(() => null);
            onAltaAbierta(null);
            setVuelta({ clase: 'error', motivo: 'proveedor' });
            setPaso('res');
            return;
        }
        setYcloud('abierta');
        await altaYCloud.abrir(enlace, (suceso: SucesoYCloud) => {
            if (numero !== ventana.current) {
                return;
            }
            if (suceso.tipo === 'estado') {
                if (suceso.conectado) {
                    setYcloud('confirmando');
                    void consultarRef.current(a.id);
                } else {
                    // G709: un fallo de la ventana (también un número no apto para la coexistencia) solo se cuenta así.
                    setYcloud('incompleta');
                }
            } else if (suceso.tipo === 'bloqueada' || suceso.tipo === 'sin-sdk') {
                setYcloud('bloqueada');
            } else if (suceso.tipo === 'cerrada') {
                setYcloud((antes) => (antes === 'confirmando' ? antes : 'cerrada'));
            } else {
                void enlaceNoValido(a);
            }
        });
    };

    const abrirVentana = (a: Alta) => (a.channel_type === 'whatsapp' ? abrirYCloud(a) : abrirMeta(a));

    const entrarConMeta = async () => {
        if (tipo === 'whatsapp' && modo === null) {
            setErrModo(true);
            return;
        }
        setOcupado(true);
        const a = await empezar();
        setOcupado(false);
        if (a === null) {
            return;
        }
        setAlta(a);
        onAltaAbierta(a);
        setPaso('meta');
        await abrirVentana(a);
    };

    const conectarTelegram = async () => {
        const err = errorToken(token);
        if (err !== null) {
            setErrToken(err);
            return;
        }
        setOcupado(true);
        const a = retomada() ?? (await empezar());
        if (a !== null) {
            await completar(a, { telegram_bot_token: token.trim() });
        }
        setOcupado(false);
    };

    /** Email (G747): solo el nombre visible; sale siempre desde `avisos@reservaria.app` y queda listo en el momento. */
    const conectarEmail = async () => {
        setOcupado(true);
        const a = retomada() ?? (await empezar());
        if (a !== null) {
            const nombre = remitente.trim();
            await completar(a, nombre === '' ? {} : { email_from_name: nombre });
        }
        setOcupado(false);
    };

    /** Un alta de Telegram o email retomada con «Seguir» (aún `started`): se completa esa en lugar de empezar otra. */
    const retomada = (): Alta | null => (alta !== null && alta.status === 'started' && alta.channel_type === tipo ? alta : null);

    /** «Seguir con la que empecé» (409 `conflict`, G687): se lee el alta abierta y se sigue donde se quedó. */
    const seguir = async (id: string) => {
        setOcupado(true);
        const a = await ops.getChannelConnection(negocio, id).catch((e: unknown) => {
            avisar({ tipo: 'error', texto: avisoBreveDeError(e) ?? 'No se ha podido abrir el alta.' });
            return null;
        });
        setOcupado(false);
        if (a === null) {
            return;
        }
        setVuelta(null);
        if (a.status === 'started' || a.status === 'awaiting_provider') {
            setAlta(a);
            if (a.channel_type === 'whatsapp' || a.channel_type === 'instagram') {
                onAltaAbierta(a);
                setPaso('meta');
            } else {
                setPaso('form');
            }
            return;
        }
        terminar(a);
    };

    const cancelar = async (id: string | null) => {
        ventana.current += 1;
        if (id !== null) {
            setOcupado(true);
            const r = await ops.cancelChannelConnection(negocio, id).catch((e: unknown) => {
                avisar({ tipo: 'error', texto: avisoBreveDeError(e) ?? 'No se ha podido cancelar.' });
                return null;
            });
            setOcupado(false);
            if (r === null) {
                return;
            }
        }
        onAltaAbierta(null);
        onCerrar('Alta cancelada. No se ha conectado nada.');
    };

    const elegir = (t: TipoCanal) => {
        const p = permitido(t, datos.bloques);
        if (!p.ok) {
            setBloqueado(bloqueado === t ? null : t);
            return;
        }
        setBloqueado(null);
        setTipo(t);
        if (sigueAbierta(t)) {
            setAlta(altaPrevia);
            setPaso('meta');
        } else {
            setPaso('form');
        }
    };

    const otraVez = () => {
        setYcloud(null);
        setVuelta(null);
        setAlta(null);
        setPaso('form');
    };

    // ── Cabecera del panel y su botón ──
    const icono: NombreIcono = tipo === null ? 'plug' : (TIPO[tipo]?.icono ?? 'plug');
    let titulo = tipo === null ? 'Elige el canal' : reemplaza !== null ? nombreDeCanal(reemplaza) : nombreTipo;
    let subtitulo: string | undefined = reemplaza !== null ? reemplaza.display_identifier : tipo === null ? 'Lo que puedes conectar con tu plan.' : undefined;
    let accion = '';
    let accionIcono: NombreIcono = 'check';
    let ocupadoTexto = 'Guardando…';
    let onAccion: () => void = () => undefined;
    const meta = tipo === 'whatsapp' || tipo === 'instagram';
    if (paso === 'form' && tipo !== null) {
        if (meta) {
            [accion, accionIcono, ocupadoTexto, onAccion] = ['Entrar con Meta', 'external-link', 'Abriendo Meta…', () => { void entrarConMeta(); }];
        } else if (tipo === 'telegram') {
            [accion, accionIcono, ocupadoTexto, onAccion] = ['Conectar', 'plug', 'Comprobando…', () => { void conectarTelegram(); }];
        } else if (tipo === 'email') {
            [accion, accionIcono, ocupadoTexto, onAccion] = ['Conectar', 'plug', 'Guardando…', () => { void conectarEmail(); }];
        }
    } else if (paso === 'meta') {
        subtitulo = tipo === 'whatsapp' ? 'Enlace de alta de YCloud' : 'Alta integrada de Meta';
        ocupadoTexto = 'Conectando…';
    }

    // ── La vuelta ──
    let res: { tono: 'blanca' | 'amarilla' | 'gris' | 'roja'; icono: NombreIcono; titulo: string; chip: [string, EstadoChip]; texto: string; datos?: [string, string | [string, EstadoChip]][]; siguiente?: boolean; botones?: { texto: string; icono: NombreIcono; clave: Pulsable; inactivo?: boolean }[] } | null = null;
    if (paso === 'res' && vuelta !== null && tipo !== null) {
        const nuevo = canalDe(alta?.channel_id) ?? reemplaza;
        if (vuelta.clase === 'correcta') {
            titulo = reemplaza !== null ? nombreDeCanal(reemplaza) : nombreTipo;
            res = {
                tono: 'blanca',
                icono: 'check',
                chip: ['Activo', 'confirmed'],
                titulo: reemplaza !== null ? `${nombreDeCanal(reemplaza)} vuelve a funcionar` : tipo === 'email' ? 'Email listo' : `${nombreTipo} conectado`,
                texto: tipo === 'whatsapp' ? 'Ya recibe mensajes. Te hemos enviado un mensaje de prueba y hemos mandado a Meta las plantillas de tus módulos.' : tipo === 'instagram' ? 'Ya recibe mensajes y los mensajes de inicio están configurados.' : tipo === 'telegram' ? 'El bot ya escucha. Escríbele desde Telegram para probarlo.' : 'Tus emails salen desde avisos@reservaria.app con el nombre de tu negocio.',
                datos: [
                    ['Canal', nuevo?.display_identifier ?? ''],
                    ...(tipo === 'whatsapp' ? ([['Conectado a través de', PROVEEDOR.ycloud ?? ''], ['Pago de los mensajes', ['Lo pone Reservaria', 'confirmed']], ['Plantillas de Reservaria', ['Enviadas a Meta', 'pending']]] as [string, string | [string, EstadoChip]][]) : []),
                    ...(tipo === 'instagram' ? ([['Mensajes de inicio', ['Configurados', 'confirmed']]] as [string, [string, EstadoChip]][]) : []),
                ],
                siguiente: tipo === 'whatsapp' && reemplaza === null,
                botones:
                    tipo === 'whatsapp'
                        ? [
                              { texto: 'Ir a Mensajes', icono: 'arrow-right', clave: 'mensajes' },
                              ...(puede('canales.plantillas.gestionar') ? [{ texto: 'Ver plantillas de Meta', icono: 'arrow-right' as const, clave: 'plantillas' as const }] : []),
                          ]
                        : [],
            };
            if (alta?.channel_id != null) {
                const id = alta.channel_id;
                [accion, accionIcono, onAccion] = ['Ver el canal', 'arrow-right', () => { onVerCanal(id); }];
            }
        } else if (vuelta.clase === 'pendiente') {
            res = {
                tono: 'amarilla',
                icono: 'hourglass',
                chip: ['En verificación', 'pending'],
                // G698: en WhatsApp, `verifying` es la espera del pago de YCloud (hasta 24 h).
                titulo: tipo === 'instagram' ? 'Meta está revisando la cuenta' : tipo === 'whatsapp' ? 'Esperando a que YCloud active el pago (hasta 24 h)' : 'Meta está verificando tu número',
                texto: tipo === 'whatsapp' ? 'El canal se activa solo en cuanto YCloud lo confirme y te avisamos en la campana. No tienes que hacer nada más.' : 'Puede tardar unas horas. El canal se activa solo y te avisamos en la campana. No tienes que hacer nada más.',
                datos: [['Canal', nuevo?.display_identifier ?? ''], ['Negocio en Meta', ['Verificando', 'pending']], ...(tipo === 'whatsapp' ? ([['Pago de los mensajes', ['Activándose', 'pending']]] as [string, [string, EstadoChip]][]) : [])],
            };
            [accion, accionIcono, onAccion] = ['Volver a Canales', 'check', () => { onCerrar(); }];
        } else if (vuelta.clase === 'cancelada') {
            res = { tono: 'gris', icono: 'x', chip: ['Cancelado', 'neutral'], titulo: 'Has cerrado la ventana de Meta', texto: 'No se ha conectado nada. Cuando quieras, vuelve a entrar: son tres pasos y no te pedimos tarjeta.' };
            [accion, accionIcono, onAccion] = ['Volver a empezar', 'refresh-cw', otraVez];
        } else if (vuelta.clase === 'caducada') {
            res = { tono: 'gris', icono: 'clock', chip: ['Caducado', 'neutral'], titulo: 'El alta ha caducado', texto: 'Pasó demasiado tiempo desde que la empezaste. No se ha conectado nada: vuelve a empezar.' };
            [accion, accionIcono, onAccion] = ['Volver a empezar', 'refresh-cw', otraVez];
        } else if (vuelta.motivo === 'alta-abierta') {
            res = {
                tono: 'amarilla',
                icono: 'hourglass',
                chip: ['A medias', 'pending'],
                titulo: `Ya hay un alta de ${nombreTipo} a medias`,
                texto: 'Solo puede haber una a la vez. Sigue con la que empezaste o cancélala para empezar otra.',
                botones:
                    vuelta.abiertaId === undefined
                        ? []
                        : [
                              { texto: 'Seguir con la que empecé', icono: 'arrow-right', clave: 'seguir' },
                              { texto: 'Cancelarla', icono: 'x', clave: 'cancelarla' },
                          ],
            };
        } else {
            const m = MOTIVOS[vuelta.motivo ?? ''] ?? MOTIVOS.verificacion;
            if (m !== undefined) {
                res = {
                    tono: 'roja',
                    icono: m.icono,
                    chip: ['No conectado', 'problem'],
                    titulo: m.titulo,
                    texto: m.texto,
                    // «Añadir a mi plan» espera al cobro (G92): se ve al Propietario, sin destino todavía.
                    botones: m.plan === true && propietario ? [{ texto: 'Añadir a mi plan', icono: 'plus', clave: 'plan', inactivo: true }] : [],
                };
                if (m.sinReintento === true) {
                    [accion, accionIcono, onAccion] = ['Volver a Canales', 'check', () => { onCerrar(); }];
                } else {
                    [accion, accionIcono, onAccion] = ['Repetir el alta', 'refresh-cw', otraVez];
                }
            }
        }
    }

    function pulsar(clave: Pulsable) {
        if (clave === 'mensajes') {
            navegar(RUTA_CANALES_DE_ENVIO);
        } else if (clave === 'plantillas') {
            onCerrar();
            irA('plantillas');
        } else if (clave === 'seguir' && vuelta?.abiertaId !== undefined) {
            void seguir(vuelta.abiertaId);
        } else if (clave === 'cancelarla') {
            void cancelar(vuelta?.abiertaId ?? null);
        }
    }

    const cerrar = () => {
        if (ocupado && paso !== 'meta') {
            return;
        }
        ventana.current += 1;
        onCerrar();
    };

    // La ventana del alta (G694, G695), con los textos de 11.1: abierta, bloqueada, cerrada y «confirmándolo…».
    const hasta = alta === null ? null : HORA.format(new Date(alta.expires_at));
    const ventanaMeta: { icono: NombreIcono; titulo: string; texto: string; pulso: boolean } =
        tipo !== 'whatsapp'
            ? { icono: 'external-link', titulo: 'Sigue en la ventana de Meta', texto: 'Cuando termines, vuelves aquí solo. No cierres esta página.', pulso: true }
            : ycloud === 'confirmando'
              ? { icono: 'hourglass', titulo: 'WhatsApp conectado en YCloud; estamos confirmándolo…', texto: 'Suele tardar menos de un minuto. No tienes que hacer nada más: esta página se actualiza sola.', pulso: true }
              : ycloud === 'bloqueada'
                ? { icono: 'shield-alert', titulo: 'Tu navegador ha bloqueado la ventana de YCloud', texto: `Permite las ventanas emergentes o ábrela en otra pestaña. Es el mismo enlace${hasta === null ? '' : ` y vale hasta las ${hasta}`}.`, pulso: false }
                : ycloud === 'cerrada'
                  ? { icono: 'app-window', titulo: 'Has cerrado la ventana', texto: `Puedes volver a abrirla${hasta === null ? '' : ` hasta las ${hasta}`} y seguir donde lo dejaste. No se ha conectado nada todavía.`, pulso: false }
                  : { icono: 'external-link', titulo: 'Sigue en la ventana de YCloud', texto: 'Dentro, pulsa «Continue with Meta». Es el alta oficial de YCloud, socio oficial de Meta. Cuando termines, vuelves aquí solo: no cierres esta página.', pulso: true };

    return (
        <PanelLateral
            abierto
            onCerrar={cerrar}
            movil={movil}
            titulo={titulo}
            eyebrow={reemplaza !== null ? 'Reconectar' : 'Conectar un canal'}
            {...(subtitulo === undefined ? {} : { subtitulo })}
            icono={icono}
            accion={accion}
            accionIcono={accionIcono}
            ocupado={ocupado && paso !== 'meta'}
            ocupadoTexto={ocupadoTexto}
            inactiva={paso === 'form' && !conecta}
            onAccion={onAccion}
        >
            <div className="rv-cn-alta">
                {paso === 'elegir' && (
                    <>
                        <div role="radiogroup" aria-label="Qué canal" className="rv-cn-tipos">
                            {TIPOS.map((t) => {
                                const p = permitido(t, datos.bloques);
                                const nombre = TIPO[t]?.nombre ?? t;
                                return (
                                    <div key={t} className="rv-cn-tipos__item">
                                        <button type="button" role="radio" aria-checked={tipo === t} className={`rv-cn-opcion${bloqueado === t ? ' rv-cn-opcion--marcada' : ''}`} onClick={() => { elegir(t); }}>
                                            <span className={`rv-cn-baldosa rv-cn-baldosa--${p.ok ? 'negra' : 'gris'}`}>
                                                <Icono nombre={TIPO[t]?.icono ?? 'plug'} />
                                            </span>
                                            <span className="rv-cn-opcion__texto">
                                                <strong style={{ color: p.ok ? 'var(--rv-ink)' : 'var(--rv-fg-2)' }}>{nombre}</strong>
                                                <small>{lineaElegir(t, p)}</small>
                                            </span>
                                            {p.chip !== null && <Chip estado={p.chip[1]}>{p.chip[0]}</Chip>}
                                        </button>
                                        {bloqueado === t && p.falta !== undefined && (
                                            <div className="rv-cn-falta">
                                                <Icono nombre="puzzle" />
                                                <span>{p.falta + (p.proximamente === true || propietario ? '' : ' Pídeselo al propietario.')}</span>
                                                {propietario && p.proximamente !== true && (
                                                    <Boton tipo="secundario" icono="plus" movil={movil} disabled>
                                                        Añadir a mi plan
                                                    </Boton>
                                                )}
                                            </div>
                                        )}
                                    </div>
                                );
                            })}
                        </div>
                        <p className="rv-ca-nota rv-ca-nota--gris">
                            <Icono nombre="shield-check" />
                            <span>Solo con las conexiones oficiales de cada servicio. Las claves se guardan cifradas y nunca se enseñan.</span>
                        </p>
                    </>
                )}
                {paso === 'form' && tipo !== null && (
                    <>
                        {reemplaza !== null && (
                            <p className="rv-cn-nota-gris" role="note">
                                <Icono nombre="refresh-cw" />
                                <span>{`Vuelves a conectar «${nombreDeCanal(reemplaza)}» (${reemplaza.display_identifier}). Conserva sus conversaciones.`}</span>
                            </p>
                        )}
                        {tipo === 'instagram' && permitido('instagram', datos.bloques).soloListas === true && (
                            <p className="rv-cn-nota-gris" role="note">
                                <Icono nombre="ticket" />
                                <span>Solo para las listas de invitados: a quien escriba con un enlace de lista le respondemos con su QR. Sin asistente, y no cuenta como conversación.</span>
                            </p>
                        )}
                        <ol className="rv-cn-pasos">
                            {(PASOS[tipo] ?? []).map(([t, b], i) => (
                                <li key={t}>
                                    <span className="rv-cn-pasos__n">{String(i + 1)}</span>
                                    <span>
                                        <strong>{t}</strong>
                                        <span>{b}</span>
                                    </span>
                                </li>
                            ))}
                        </ol>
                        {tipo === 'whatsapp' && (
                            <div role="radiogroup" aria-label="¿Ya usas este número en la app WhatsApp Business?" className="rv-cn-modo">
                                <strong>¿Ya usas este número en la app WhatsApp Business?</strong>
                                <OpcionRadio elegida={modo === 'business_app'} onElegir={() => { setModo('business_app'); setErrModo(false); }} titulo="Sí, y quiero seguir usándola" />
                                <OpcionRadio elegida={modo === 'api_only'} onElegir={() => { setModo('api_only'); setErrModo(false); }} titulo="No" />
                                {errModo && <p className="rv-cn-error" role="alert">Elige una opción para seguir.</p>}
                                {modo === 'business_app' && (
                                    <p className="rv-cn-nota-gris" role="note">
                                        <Icono nombre="info" />
                                        <span>
                                            Seguirás usando la app en tu móvil. Ten el móvil a mano: tendrás que escanear un código con la app. Lo que contestes desde la app no pasa por el asistente.{' '}
                                            <button type="button" className="rv-cn-enlace" aria-expanded={queCambia} onClick={() => { setQueCambia(!queCambia); }}>
                                                Qué cambia
                                            </button>
                                        </span>
                                    </p>
                                )}
                                {modo === 'business_app' && queCambia && (
                                    <ul className="rv-cn-que-cambia">
                                        <li>Los envíos van más despacio: unos 5 mensajes por segundo.</li>
                                        <li>La app tiene que seguir en uso; si no, WhatsApp puede desconectarla.</li>
                                        <li>Lo que envíes desde la app no gasta mensajes de tu paquete; solo lo que sale por Reservaria.</li>
                                        <li>El historial solo llega si lo compartes, y no entero.</li>
                                    </ul>
                                )}
                            </div>
                        )}
                        {tipo === 'telegram' && (
                            <Campo tipo="password" etiqueta="Token del bot" valor={token} error={errToken} ayuda="Lo da @BotFather. Se guarda cifrado." autocompletar="off" onChange={(v) => { setToken(v); setErrToken(null); }} nombre="telegram-token" />
                        )}
                        {tipo === 'email' && <Campo etiqueta="Nombre del remitente" valor={remitente} max={80} ayuda="Lo que ven tus clientes en «De»." onChange={setRemitente} nombre="email-remitente" />}
                        {reemplaza === null && tipo !== 'email' && (
                            <Desplegable etiqueta="Local" valor={sede} opciones={[{ valor: '', texto: 'Todos los locales' }, ...datos.sedes.map((s) => ({ valor: s.id, texto: s.nombre }))]} ayuda="Opcional. Para tener uno por local." onChange={setSede} nombre="conectar-local" />
                        )}
                        {(NOTAS_ALTA[tipo] ?? []).map(([i, t]) => (
                            <p key={t} className="rv-ca-nota rv-ca-nota--gris">
                                <Icono nombre={i} />
                                <span>{t}</span>
                            </p>
                        ))}
                    </>
                )}
                {paso === 'meta' && tipo !== null && (
                    <>
                        <div className="rv-cn-ventana" role="status">
                            <span className="rv-cn-ventana__baldosa">
                                <Icono nombre={ventanaMeta.icono} />
                            </span>
                            <strong>{ventanaMeta.titulo}</strong>
                            <span>{ventanaMeta.texto}</span>
                            {ventanaMeta.pulso && (
                                <span className="rv-cn-puntos" aria-hidden="true">
                                    <i />
                                    <i />
                                    <i />
                                </span>
                            )}
                        </div>
                        {tipo === 'whatsapp' && ycloud === 'bloqueada' && alta?.ycloud_onboarding != null && (
                            <div className="rv-cn-nota-amarilla" role="note">
                                <Icono nombre="info" />
                                <span>
                                    <a href={alta.ycloud_onboarding.onboarding_url} target="_blank" rel="noopener noreferrer">
                                        Abrir en otra pestaña
                                    </a>
                                </span>
                            </div>
                        )}
                        {tipo === 'whatsapp' && ycloud === 'incompleta' && (
                            <p className="rv-cn-nota-gris" role="note">
                                <Icono nombre="info" />
                                <span>No se ha completado; puedes intentarlo otra vez.</span>
                            </p>
                        )}
                        <ol className="rv-cn-pasos rv-cn-pasos--borde">
                            {(PASOS[tipo] ?? []).map(([t], i) => (
                                <li key={t}>
                                    <span className="rv-cn-pasos__n">{String(i + 1)}</span>
                                    <strong>{t}</strong>
                                </li>
                            ))}
                        </ol>
                        {alta !== null && (
                            <p className="rv-ca-nota rv-ca-nota--gris">
                                <Icono nombre="clock" />
                                <span>{`Tienes hasta las ${HORA.format(new Date(alta.expires_at))} para terminar. Si se pasa la hora, el alta caduca y se empieza otra vez.`}</span>
                            </p>
                        )}
                        {/* Confirmando (G695): sin botones; la página se actualiza sola. */}
                        {ycloud !== 'confirmando' && (
                        <div className="rv-cn-botones">
                            <Boton tipo="secundario" icono="external-link" movil={movil} disabled={alta === null || ocupado} onClick={() => { if (alta !== null) { void abrirVentana(alta); } }}>
                                {tipo === 'whatsapp' ? 'Abrir otra vez' : 'Abrir Meta otra vez'}
                            </Boton>
                            <Boton tipo="secundario" rojo icono="x" movil={movil} disabled={ocupado} onClick={() => { void cancelar(alta?.id ?? null); }}>
                                Cancelar el alta
                            </Boton>
                        </div>
                        )}
                    </>
                )}
                {res !== null && (
                    <>
                        <div className={`rv-cn-vuelta rv-cn-vuelta--${res.tono}`}>
                            <span className="rv-cn-vuelta__baldosa">
                                <Icono nombre={res.icono} />
                            </span>
                            <span className="rv-cn-vuelta__titulo">
                                <strong>{res.titulo}</strong>
                                <Chip estado={res.chip[1]}>{res.chip[0]}</Chip>
                            </span>
                            <span>{res.texto}</span>
                        </div>
                        {res.siguiente === true && (
                            <div className="rv-cn-nota-gris" role="note">
                                <Icono nombre="send" />
                                <span>
                                    <strong>Tus envíos aún no salen por WhatsApp</strong> Confirmaciones, pases y recordatorios siguen por email hasta que enciendas «Enviar por WhatsApp» en Mensajes. Cada envío por WhatsApp gasta mensajes de tu plan.
                                </span>
                            </div>
                        )}
                        {res.datos !== undefined && (
                            <div className="rv-ca-ventana-lineas">
                                {res.datos.map(([k, v]) => (
                                    <div key={k} className="rv-ca-ventana-linea">
                                        <span>{k}</span>
                                        {typeof v === 'string' ? <strong>{v}</strong> : <Chip estado={v[1]}>{v[0]}</Chip>}
                                    </div>
                                ))}
                            </div>
                        )}
                        {(res.botones ?? []).length > 0 && (
                            <div className="rv-cn-botones">
                                {(res.botones ?? []).map((b) => (
                                    <Boton key={b.texto} tipo="secundario" icono={b.icono} movil={movil} disabled={b.inactivo === true || ocupado} onClick={() => { pulsar(b.clave); }}>
                                        {b.texto}
                                    </Boton>
                                ))}
                            </div>
                        )}
                    </>
                )}
            </div>
        </PanelLateral>
    );
}

function nombreDeCanal(c: Canal): string {
    return c.display_name != null && c.display_name !== '' ? c.display_name : (TIPO[c.channel_type]?.nombre ?? c.channel_type);
}
